import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Loader2, KanbanSquare, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import api from "../../services/api";
import toastError from "../../errors/toastError";

// Abrir o cartão do cliente sem sair da conversa.
//
// É onde o trabalho costuma nascer: o cliente pede pelo WhatsApp, e o pedido
// tem de virar cartão ali mesmo — não depois, noutra tela, de memória.
const AdicionarAoKanbanModal = ({ open, onClose, contact }) => {
	const navigate = useNavigate();

	const [colunas, setColunas] = useState([]);
	const [existentes, setExistentes] = useState([]);
	const [columnId, setColumnId] = useState(null);
	const [title, setTitle] = useState("");
	const [carregando, setCarregando] = useState(true);
	const [salvando, setSalvando] = useState(false);

	useEffect(() => {
		if (!open || !contact?.id) return;

		setCarregando(true);
		(async () => {
			try {
				const [quadro, doContato] = await Promise.all([
					api.get("/kanban"),
					api.get(`/kanban/cards/by-contact/${contact.id}`),
				]);

				setColunas(quadro.data || []);
				setExistentes(doContato.data || []);
				setColumnId(quadro.data?.[0]?.id || null);
				setTitle(contact.name || contact.number || "");
			} catch (err) {
				toastError(err);
			}
			setCarregando(false);
		})();
	}, [open, contact]);

	const criar = async () => {
		if (!columnId) {
			toast.error("Crie uma coluna no Kanban antes.");
			return;
		}

		setSalvando(true);
		try {
			await api.post("/kanban/cards", {
				columnId,
				contactId: contact.id,
				title,
			});
			toast.success("Cartão criado no Kanban.");
			onClose();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const colunaEscolhida = colunas.find((c) => c.id === columnId);

	return (
		<Dialog open={open} onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Adicionar ao Kanban</DialogTitle>
					<DialogDescription>
						Abre um cartão deste cliente no quadro, sem sair da conversa.
					</DialogDescription>
				</DialogHeader>

				{carregando ? (
					<div className="flex justify-center py-6">
						<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
					</div>
				) : !colunas.length ? (
					<div className="space-y-3 py-2 text-center">
						<KanbanSquare className="mx-auto h-8 w-8 text-muted-foreground" />
						<p className="text-sm text-muted-foreground">
							O quadro ainda não tem colunas. Crie a primeira e volte aqui.
						</p>
						<Button variant="outline" onClick={() => navigate("/kanban")}>
							<ExternalLink className="h-4 w-4" />
							Abrir o Kanban
						</Button>
					</div>
				) : (
					<div className="space-y-4">
						{existentes.length > 0 && (
							<div className="rounded-lg border bg-muted/40 p-2.5 text-xs">
								<p className="font-medium">
									Este cliente já tem {existentes.length} cartão(ões):
								</p>
								<ul className="mt-1 space-y-0.5 text-muted-foreground">
									{existentes.slice(0, 3).map((c) => (
										<li key={c.id} className="truncate">
											{c.title} — {c.kanbanColumn?.name}
										</li>
									))}
								</ul>
							</div>
						)}

						<div className="space-y-1.5">
							<Label htmlFor="kanban-titulo">Título do cartão</Label>
							<Input
								id="kanban-titulo"
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								placeholder="Ex.: 200 camisetas — pedido 4312"
							/>
						</div>

						<div className="space-y-1.5">
							<Label>Coluna</Label>
							<Select
								value={columnId ? String(columnId) : undefined}
								onValueChange={(v) => setColumnId(Number(v))}
							>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{colunas.map((c) => (
										<SelectItem key={c.id} value={String(c.id)}>
											{c.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							{colunaEscolhida?.triggerMessage && (
								<p className="text-xs leading-relaxed text-primary">
									O cliente vai receber: "{colunaEscolhida.triggerMessage}"
								</p>
							)}
						</div>
					</div>
				)}

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={salvando}>
						Fechar
					</Button>
					{colunas.length > 0 && (
						<Button onClick={criar} disabled={salvando || carregando}>
							{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
							Criar cartão
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default AdicionarAoKanbanModal;
